import React, { useState } from "react";
import {
  HiOutlineExclamationTriangle,
  HiOutlineLightBulb,
  HiOutlineChatBubbleLeftEllipsis,
} from "react-icons/hi2";
import { ModalDialog, FormButton } from "../../ui";
import { usersApi } from "../../api";
import { handleFailedRequest, notify } from "../../util";
import { IResponseError } from "../../data-access";
import "./feedback-modal.scss";

interface IFeedbackModalProps {
  onClose: () => void;
}

type FeedbackType = "bug" | "feature" | "general";

const feedbackTypes: {
  value: FeedbackType;
  label: string;
  icon: React.ReactNode;
  color: string;
}[] = [
  {
    value: "general",
    label: "General",
    icon: <HiOutlineChatBubbleLeftEllipsis size={16} />,
    color: "green",
  },
  {
    value: "feature",
    label: "Feature Idea",
    icon: <HiOutlineLightBulb size={16} />,
    color: "blue",
  },
  {
    value: "bug",
    label: "Bug Report",
    icon: <HiOutlineExclamationTriangle size={16} />,
    color: "red",
  },
];

const MAX_LENGTH = 500;

const placeholders: Record<FeedbackType, string> = {
  bug: "Describe what happened, what you expected, and steps to reproduce...",
  feature: "Describe the feature you'd like to see and how it would help...",
  general: "Share your thoughts, suggestions, or anything on your mind...",
};

export const FeedbackModal = ({ onClose }: IFeedbackModalProps): JSX.Element => {
  const [type, setType] = useState<FeedbackType>("general");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = message.trim().length >= 10 && !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    usersApi
      .sendFeedback({ type, message: message.trim() })
      .then(() => {
        notify("success", "Thank you! Your feedback has been sent.");
        onClose();
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <ModalDialog
      className="feedback-modal"
      title="Share Your Feedback"
      isOpen={true}
      onCancel={onClose}
    >
      <div className="feedback-modal__content">
        <p className="feedback-modal__subtitle">
          Help us improve Upwex. Share your thoughts, suggest a feature, or report a bug.
        </p>

        <div className="feedback-modal__types">
          {feedbackTypes.map((ft) => (
            <button
              key={ft.value}
              className={`feedback-modal__type feedback-modal__type--${ft.color}${type === ft.value ? " feedback-modal__type--active" : ""}`}
              onClick={() => setType(ft.value)}
            >
              {ft.icon}
              <span>{ft.label}</span>
            </button>
          ))}
        </div>

        <div className="feedback-modal__field">
          <textarea
            className="feedback-modal__textarea"
            placeholder={placeholders[type]}
            value={message}
            onChange={(e) =>
              setMessage(e.target.value.slice(0, MAX_LENGTH))
            }
            rows={5}
            disabled={isSubmitting}
          />
          <span className="feedback-modal__counter">
            {message.length}/{MAX_LENGTH}
          </span>
        </div>

        <FormButton
          className="feedback-modal__submit"
          theme="green-light"
          htmlType="button"
          loading={isSubmitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Send Feedback
        </FormButton>
      </div>
    </ModalDialog>
  );
};
